import React from 'react';
import { Pagination } from '@mui/material';

export interface AppTablePaginationProps {
    /** Total number of items */
    totalRows: number;
    /** Current page (0-based) */
    page: number;
    /** Items per page */
    pageSize: number;
    /** Called with (event, newPage) - newPage is 1-based */
    onPageChange: (event: React.ChangeEvent<unknown>, newPage: number) => void;
    /** Called when page size select changes */
    onPageSizeChange?: (newPageSize: number) => void;
    /** Page size options for select; if not provided, no select is shown */
    pageSizeOptions?: number[];
}

const defaultPageSizeOptions = [10, 25, 50, 100];

export const AppTablePagination: React.FC<AppTablePaginationProps> = ({
    totalRows,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = defaultPageSizeOptions,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const from = totalRows === 0 ? 0 : page * pageSize + 1;
    const to = Math.min((page + 1) * pageSize, totalRows);

    return (
        <div className="flex items-center flex-wrap gap-[20px] justify-center md:justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-700">
                {onPageSizeChange && pageSizeOptions.length > 0 && (
                    <>
                        <span>Items per page:</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </>
                )}
                <span className={onPageSizeChange ? 'ml-4' : ''}>
                    {from} - {to} of {totalRows}
                </span>
            </div>
            <Pagination
                count={totalPages}
                page={page + 1}
                onChange={onPageChange}
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        borderRadius: '6px',
                    },
                    '& .Mui-selected': {
                        backgroundColor: '#0EA5E9',
                        color: '#fff',
                    },
                }}
            />
        </div>
    );
};

export default AppTablePagination;
